import { useEffect, useState } from 'react'
import { Command, CommandGroup } from 'cmdk'
import * as RadixDialog from '@radix-ui/react-dialog'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  AnalyticsIcon,
  ChainIcon,
  ConfigurationsIcon,
  BulbIcon,
  SearchIcon
} from '@itsrever/design-system'

const menuData = [
  {
    titleKey: 'command_palette.suggestions',
    icon: BulbIcon,
    items: [
      { titleKey: 'drawer_pages.orders', path: '/returns/orders' },
      { titleKey: 'drawer_pages.items', path: '/returns/items' }
    ]
  },
  {
    titleKey: 'drawer_pages.analytics',
    icon: AnalyticsIcon,
    items: [
      {
        titleKey: 'drawer_pages.compensation',
        path: '/analytics/compensation'
      },
      {
        titleKey: 'drawer_pages.return-rates',
        path: '/analytics/return-rates'
      },
      {
        titleKey: 'drawer_pages.product-analytics',
        path: '/analytics/products'
      },
      {
        titleKey: 'drawer_pages.product-return-rates',
        path: '/analytics/products-return-rates'
      },
      {
        titleKey: 'drawer_pages.all-products',
        path: '/analytics/all-products'
      }
    ]
  },
  {
    titleKey: 'drawer_pages.configuration',
    icon: ConfigurationsIcon,
    items: [
      {
        titleKey: 'drawer_pages.return-conditions',
        path: '/configuration/return-conditions'
      },
      { titleKey: 'drawer_pages.reasons', path: '/configuration/reasons' },
      {
        titleKey: 'drawer_pages.shipping',
        path: '/configuration/shipping'
      },
      {
        titleKey: 'drawer_pages.custom-theme',
        path: '/configuration/theme'
      },
      {
        titleKey: 'drawer_pages.custom-copies',
        path: '/configuration/copies'
      }
    ]
  }
]

const CustomCommandPalette = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault()
        setOpen((open) => !open)
      }
    }

    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  const onSelectPage = (route: string) => {
    navigate(route)
    setOpen(false)
  }

  const ArrowIcon = (
    <span className="text-slate-400">
      <svg
        width="15"
        height="15"
        viewBox="0 0 15 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M8.14645 3.14645C8.34171 2.95118 8.65829 2.95118 8.85355 3.14645L12.8536 7.14645C13.0488 7.34171 13.0488 7.65829 12.8536 7.85355L8.85355 11.8536C8.65829 12.0488 8.34171 12.0488 8.14645 11.8536C7.95118 11.6583 7.95118 11.3417 8.14645 11.1464L11.2929 8H2.5C2.22386 8 2 7.77614 2 7.5C2 7.22386 2.22386 7 2.5 7H11.2929L8.14645 3.85355C7.95118 3.65829 7.95118 3.34171 8.14645 3.14645Z"
          fill="currentColor"
          fillRule="evenodd"
          clipRule="evenodd"
        ></path>
      </svg>
    </span>
  )

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Global Command Menu"
      overlayClassName="fixed inset-0 bg-black/50 backdrop-blur-sm transitiona-all duration-1000"
      className={
        'absolute left-1/2 top-40 z-50 grid w-[600px] -translate-x-1/2 rounded-xl bg-white/95 shadow-xl transition-all duration-300'
      }
    >
      <RadixDialog.Title className="hidden">
        {t('command_palette.title')}
      </RadixDialog.Title>
      <div className="flex w-full items-center gap-2 border-b border-solid border-slate-300 p-3">
        <SearchIcon className="size-5 text-slate-400" />
        <Command.Input
          placeholder={t('search_component.search')}
          className="w-full bg-transparent font-light text-slate-700 focus:outline-none"
        />
      </div>
      <Command.List className="max-h-96 overflow-y-scroll px-3">
        <Command.Empty>
          <p className="px-2 py-4 font-light text-slate-600">
            {t('command_palette.no_results')}
          </p>
        </Command.Empty>
        {menuData.map((category) => (
          <CommandGroup
            key={category.titleKey}
            heading={
              <p className="mb-2 ml-2 flex items-center gap-2 text-sm text-slate-600">
                <category.icon className="size-4" />
                {t(category.titleKey)}
              </p>
            }
            className="my-4"
          >
            {category.items.map((item) => (
              <Command.Item
                key={item.path}
                onSelect={() => onSelectPage(item.path)}
                className="group flex cursor-pointer items-center justify-between rounded-md p-2 data-[selected=true]:bg-slate-400/20"
              >
                <div className="flex items-center gap-2">
                  {ArrowIcon}
                  <p className="flex items-center gap-2 font-light text-slate-700">
                    {t(item.titleKey)}
                  </p>
                </div>
                <p className="invisible flex items-center gap-1 rounded border border-solid border-slate-400 px-2 py-0.5 text-xs font-normal text-slate-500 group-data-[selected=true]:visible">
                  <ChainIcon className="size-4 rotate-12" />
                  {t('command_palette.open')}
                </p>
              </Command.Item>
            ))}
          </CommandGroup>
        ))}
      </Command.List>
    </Command.Dialog>
  )
}

export default CustomCommandPalette
